/**
 * The classic cabinet's login wall + paywall stub (sc-6940).
 *
 * DOM overlay (the '84 cabinet renders gameplay to canvas, but auth UI is
 * DOM — same convention as the existing login-modal / install-overlay).
 * Reuses the `.login-modal*` CSS family plus a small `.play-gate*` set for
 * the Google button + pitch line.
 *
 * Login wall: sells the 5 free daily tokens, offers [Continue with Google]
 * (primary, low-friction) and an email fallback (magic-link today;
 * email-OTP fast-follow when Zyphr ships it, M2v-2a).
 *
 * Both auth paths leave the page (Google redirect / magic-link email), so
 * we stash the cartridge the player was trying to coin and relaunch it
 * after the /auth/callback round-trip (consumePendingLaunch()).
 *
 * Paywall (grant:'paywall') is the real pricing surface (sc-6943): three
 * passes (day $0.99 / week $2.99 / month $8.99), one-tap buy → PayPal
 * approve redirect. The buyer returns to /billing/return, which captures
 * (day pass) and relaunches the pending cartridge.
 */

import { PASS_CATALOG, type PassOption } from '@arcraz/arcadia-types';
import { oauthStart, startCheckout } from '../api/client.js';
import { sendEmailOtp, sendMagicLink, verifyEmailOtp } from '@arcraz/arcadia-auth-web';
import { establishSession } from '../auth/establish.js';

/** Cabinet paths PayPal returns the buyer to (handled on boot). */
const BILLING_RETURN_PATH = '/billing/return';
const BILLING_CANCEL_PATH = '/billing/cancel';

const PENDING_LAUNCH_KEY = 'a84.pendingLaunch';
const OAUTH_STATE_KEY = 'a84.oauth.state';
const OAUTH_PROVIDER_KEY = 'a84.oauth.provider';

/** Remember the cartridge to auto-launch after the auth round-trip. */
export function setPendingLaunch(cartridgeId: string): void {
  try {
    sessionStorage.setItem(PENDING_LAUNCH_KEY, cartridgeId);
  } catch {
    /* private mode — auto-relaunch just won't happen; login still works */
  }
}

/** Read + clear the pending cartridge (called on boot after callback). */
export function consumePendingLaunch(): string | null {
  try {
    const id = sessionStorage.getItem(PENDING_LAUNCH_KEY);
    sessionStorage.removeItem(PENDING_LAUNCH_KEY);
    return id;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- overlay

let activeWall: HTMLElement | null = null;

function dismiss(): void {
  activeWall?.remove();
  activeWall = null;
}

/**
 * Show the login wall. Returns a promise that resolves when the player
 * dismisses it (back to selector); the auth actions navigate away, so
 * those paths never resolve here.
 *
 * Email path is EMAIL-OTP (sc-7039): email → 6-digit code → signed in in
 * place. Magic-link demoted to a secondary fallback. `onSignedIn` fires
 * only for the in-place OTP path (Google/magic-link leave the page and
 * resume via /auth/callback).
 */
export function showLoginWall(
  cartridgeId?: string,
  cartridgeTitle?: string,
  onSignedIn?: () => void
): Promise<void> {
  return new Promise((resolve) => {
    dismiss();
    const refs = buildWall('login');
    activeWall = refs.root;
    document.body.appendChild(refs.root);
    setTimeout(() => refs.googleBtn?.focus(), 0);

    const close = (): void => {
      dismiss();
      document.removeEventListener('keydown', onKeydown, true);
      resolve();
    };
    const onKeydown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        // Don't bubble to the cabinet's Escape/pause listeners.
        e.stopPropagation();
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKeydown, true);
    refs.closeBtn.addEventListener('click', close);
    refs.root.addEventListener('click', (e) => {
      if (e.target === refs.root) close();
    });

    // Google: stash the pending cartridge + provider, ask the API for the
    // authorization URL, then redirect. `state` is stashed for the
    // callback's CSRF check.
    refs.googleBtn?.addEventListener('click', () => {
      void (async (): Promise<void> => {
        if (!refs.googleBtn) return;
        refs.googleBtn.disabled = true;
        refs.status.textContent = 'Redirecting to Google…';
        try {
          if (cartridgeId) setPendingLaunch(cartridgeId);
          const { authorizationUrl, state } = await oauthStart(
            'google',
            `${window.location.origin}/auth/callback`
          );
          sessionStorage.setItem(OAUTH_STATE_KEY, state);
          sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'google');
          window.location.assign(authorizationUrl);
        } catch (err) {
          refs.googleBtn.disabled = false;
          refs.status.textContent =
            err instanceof Error
              ? `Couldn't start Google sign-in: ${err.message}`
              : 'Sign-in failed.';
        }
      })();
    });

    // Email: reveal the email input (step 1 of the OTP flow).
    refs.emailBtn?.addEventListener('click', () => {
      if (refs.emailForm) refs.emailForm.hidden = false;
      refs.emailInput?.focus();
    });

    let otpEmail = '';
    const emailOk = (e: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

    // Step 1 — send the OTP code.
    const sendCode = (): void => {
      const email = refs.emailInput?.value.trim() ?? '';
      if (!emailOk(email)) {
        refs.status.textContent = 'Enter a valid email address.';
        refs.emailInput?.focus();
        return;
      }
      refs.status.textContent = 'Sending your code…';
      if (refs.emailSend) refs.emailSend.disabled = true;
      void sendEmailOtp(email)
        .then(() => {
          otpEmail = email;
          if (refs.emailForm) refs.emailForm.hidden = true;
          if (refs.codeForm) refs.codeForm.hidden = false;
          if (refs.codeInput) refs.codeInput.value = '';
          refs.codeInput?.focus();
          refs.status.textContent = `We emailed a 6-digit code to ${email}.`;
        })
        .catch((err) => {
          if (refs.emailSend) refs.emailSend.disabled = false;
          refs.status.textContent =
            err instanceof Error ? `Couldn't send the code: ${err.message}` : 'Send failed.';
        });
    };
    refs.emailSend?.addEventListener('click', sendCode);
    refs.emailInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); sendCode(); }
    });

    // Step 2 — verify the code → session established IN PLACE.
    const verifyCode = (): void => {
      const code = refs.codeInput?.value.trim() ?? '';
      if (code.length < 4) {
        refs.status.textContent = 'Enter the code from your email.';
        refs.codeInput?.focus();
        return;
      }
      refs.status.textContent = 'Verifying…';
      if (refs.codeVerify) refs.codeVerify.disabled = true;
      void verifyEmailOtp({ email: otpEmail, code })
        .then((session) => {
          establishSession(session);
          refs.status.textContent = 'Signed in!';
          dismiss();
          document.removeEventListener('keydown', onKeydown, true);
          resolve();
          onSignedIn?.();
        })
        .catch((err) => {
          if (refs.codeVerify) refs.codeVerify.disabled = false;
          refs.status.textContent =
            err instanceof Error ? `That didn't work: ${err.message}` : 'Verification failed.';
          refs.codeInput?.focus();
        });
    };
    refs.codeVerify?.addEventListener('click', verifyCode);
    refs.codeInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); verifyCode(); }
    });

    // Secondary fallback — magic link instead of a code (leaves the page).
    refs.magicLinkBtn?.addEventListener('click', () => {
      const email = (refs.emailInput?.value || otpEmail).trim();
      if (!emailOk(email)) {
        if (refs.emailForm) refs.emailForm.hidden = false;
        refs.status.textContent = 'Enter your email first, then tap the link option.';
        refs.emailInput?.focus();
        return;
      }
      refs.status.textContent = 'Sending your sign-in link…';
      if (refs.magicLinkBtn) refs.magicLinkBtn.disabled = true;
      if (cartridgeId) setPendingLaunch(cartridgeId);
      void sendMagicLink({ email, redirectUrl: `${window.location.origin}/auth/callback` })
        .then(() => {
          refs.status.textContent = 'Check your email for a sign-in link.';
        })
        .catch((err) => {
          if (refs.magicLinkBtn) refs.magicLinkBtn.disabled = false;
          refs.status.textContent =
            err instanceof Error ? `Couldn't send the link: ${err.message}` : 'Send failed.';
        });
    });
    void cartridgeTitle; // reserved for a future "…to play NEON OVERPASS" line
  });
}

/**
 * Paywall (grant:'paywall') — real pricing (sc-6943). Renders the three
 * passes; a Buy tap starts a PayPal checkout and redirects to the approve
 * page. The pending cartridge is stashed first so /billing/return can
 * relaunch it. Buy paths leave the page, so the promise only resolves on
 * dismiss (same contract as the login wall's Google path).
 */
export function showPaywall(resetsAtUtc?: string, cartridgeId?: string): Promise<void> {
  return new Promise((resolve) => {
    dismiss();
    const refs = buildWall('paywall');
    activeWall = refs.root;
    if (resetsAtUtc) {
      const reset = new Date(resetsAtUtc);
      const mins = Math.max(0, Math.round((reset.getTime() - Date.now()) / 60000));
      const h = Math.floor(mins / 60);
      refs.status.textContent =
        h > 0
          ? `Free tokens refill in about ${h}h ${mins % 60}m.`
          : `Free tokens refill in about ${mins}m.`;
    }

    // Build the pricing tiers and insert them above the status line.
    const tiers = document.createElement('div');
    tiers.className = 'play-gate__tiers';
    let busy = false;

    const buy = (pass: PassOption): void => {
      if (busy) return;
      busy = true;
      for (const b of tiers.querySelectorAll('button')) b.disabled = true;
      refs.status.textContent = `Starting checkout for the ${pass.label}…`;
      if (cartridgeId) setPendingLaunch(cartridgeId);
      const origin = window.location.origin;
      const returnUrl = `${origin}${BILLING_RETURN_PATH}?kind=${encodeURIComponent(pass.kind)}`;
      const cancelUrl = `${origin}${BILLING_CANCEL_PATH}`;
      void startCheckout(pass.kind, returnUrl, cancelUrl)
        .then((res) => {
          if (!res.approveUrl) throw new Error('No approve URL returned');
          window.location.assign(res.approveUrl);
        })
        .catch((err) => {
          busy = false;
          for (const b of tiers.querySelectorAll('button')) b.disabled = false;
          refs.status.textContent =
            err instanceof Error ? `Couldn't start checkout: ${err.message}` : 'Checkout failed.';
        });
    };

    for (const pass of PASS_CATALOG) tiers.append(buildTier(pass, buy));
    // status is the last thing buildWall appended before the close button;
    // insert tiers just before it.
    refs.status.parentElement?.insertBefore(tiers, refs.status);

    document.body.appendChild(refs.root);
    setTimeout(() => refs.closeBtn.focus(), 0);

    const close = (): void => {
      dismiss();
      document.removeEventListener('keydown', onKeydown, true);
      resolve();
    };
    const onKeydown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKeydown, true);
    refs.closeBtn.addEventListener('click', close);
    refs.root.addEventListener('click', (e) => {
      if (e.target === refs.root) close();
    });
  });
}

/** One pricing tier: label, price + cadence, blurb, and a Buy button. */
function buildTier(pass: PassOption, onBuy: (pass: PassOption) => void): HTMLElement {
  const tier = document.createElement('div');
  tier.className = pass.featured
    ? 'play-gate__tier play-gate__tier--featured'
    : 'play-gate__tier';

  if (pass.featured) {
    const badge = document.createElement('span');
    badge.className = 'play-gate__tier-badge';
    badge.textContent = 'BEST VALUE';
    tier.append(badge);
  }

  const head = document.createElement('div');
  head.className = 'play-gate__tier-head';
  const label = document.createElement('span');
  label.className = 'play-gate__tier-label';
  label.textContent = pass.label;
  const price = document.createElement('span');
  price.className = 'play-gate__tier-price';
  price.textContent = `$${pass.priceUsd}`;
  head.append(label, price);

  const cadence = document.createElement('div');
  cadence.className = 'play-gate__tier-cadence';
  cadence.textContent = pass.cadence;

  const blurb = document.createElement('p');
  blurb.className = 'play-gate__tier-blurb';
  blurb.textContent = pass.blurb;

  const buyBtn = document.createElement('button');
  buyBtn.type = 'button';
  buyBtn.className = 'login-modal__btn login-modal__btn--primary play-gate__tier-buy';
  buyBtn.textContent = `Get ${pass.label}`;
  buyBtn.addEventListener('click', () => onBuy(pass));

  tier.append(head, cadence, blurb, buyBtn);
  return tier;
}

/**
 * Back-compat alias: the gate calls showPaywallStub in a couple of places
 * (fail-closed 'error' branch has no cartridge id). Forwards to the real
 * paywall (sc-6943).
 */
export function showPaywallStub(resetsAtUtc?: string): Promise<void> {
  return showPaywall(resetsAtUtc);
}

// ---------------------------------------------------------------- DOM

interface WallRefs {
  root: HTMLElement;
  googleBtn: HTMLButtonElement | null;
  emailBtn: HTMLButtonElement | null;
  emailForm: HTMLElement | null;
  emailInput: HTMLInputElement | null;
  emailSend: HTMLButtonElement | null;
  codeForm: HTMLElement | null;
  codeInput: HTMLInputElement | null;
  codeVerify: HTMLButtonElement | null;
  magicLinkBtn: HTMLButtonElement | null;
  status: HTMLParagraphElement;
  closeBtn: HTMLButtonElement;
}

function buildWall(kind: 'login' | 'paywall'): WallRefs {
  const root = document.createElement('div');
  root.className = 'login-modal play-gate';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-labelledby', 'play-gate-title');

  const card = document.createElement('div');
  card.className = 'login-modal__card';

  const eyebrow = document.createElement('div');
  eyebrow.className = 'play-gate__eyebrow';
  eyebrow.textContent = kind === 'login' ? 'INSERT COIN' : 'OUT OF TOKENS';

  const title = document.createElement('h2');
  title.id = 'play-gate-title';
  title.className = 'login-modal__title';
  title.textContent = kind === 'login' ? 'SIGN IN TO KEEP PLAYING' : "YOU'RE OUT OF TOKENS";

  const pitch = document.createElement('p');
  pitch.className = 'login-modal__body';
  pitch.textContent =
    kind === 'login'
      ? 'Free account = 5 plays every day. No card, no catch.'
      : 'Come back tomorrow for 5 more — or grab a pass for unlimited play right now.';

  card.append(eyebrow, title, pitch);

  let googleBtn: HTMLButtonElement | null = null;
  let emailBtn: HTMLButtonElement | null = null;
  let emailForm: HTMLElement | null = null;
  let emailInput: HTMLInputElement | null = null;
  let emailSend: HTMLButtonElement | null = null;
  let codeForm: HTMLElement | null = null;
  let codeInput: HTMLInputElement | null = null;
  let codeVerify: HTMLButtonElement | null = null;
  let magicLinkBtn: HTMLButtonElement | null = null;

  if (kind === 'login') {
    googleBtn = document.createElement('button');
    googleBtn.type = 'button';
    googleBtn.className = 'play-gate__google';
    googleBtn.textContent = 'Continue with Google';

    emailBtn = document.createElement('button');
    emailBtn.type = 'button';
    emailBtn.className = 'play-gate__email-toggle';
    emailBtn.textContent = 'Use email instead';

    // Step 1: email → "Send code".
    emailForm = document.createElement('div');
    emailForm.className = 'play-gate__email-form';
    emailForm.hidden = true;
    emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.placeholder = 'you@example.com';
    emailInput.autocomplete = 'email';
    emailInput.className = 'login-modal__input';
    emailInput.setAttribute('aria-label', 'Email address');
    emailSend = document.createElement('button');
    emailSend.type = 'button';
    emailSend.className = 'login-modal__btn login-modal__btn--primary';
    emailSend.textContent = 'Send code';
    emailForm.append(emailInput, emailSend);

    // Step 2: 6-digit code → "Verify". Revealed after send.
    codeForm = document.createElement('div');
    codeForm.className = 'play-gate__email-form';
    codeForm.hidden = true;
    codeInput = document.createElement('input');
    codeInput.type = 'text';
    codeInput.inputMode = 'numeric';
    codeInput.autocomplete = 'one-time-code';
    codeInput.maxLength = 8;
    codeInput.placeholder = '6-digit code';
    codeInput.className = 'login-modal__input';
    codeInput.setAttribute('aria-label', 'Email code');
    codeVerify = document.createElement('button');
    codeVerify.type = 'button';
    codeVerify.className = 'login-modal__btn login-modal__btn--primary';
    codeVerify.textContent = 'Verify';
    codeForm.append(codeInput, codeVerify);

    // Secondary fallback: magic link instead of a code.
    magicLinkBtn = document.createElement('button');
    magicLinkBtn.type = 'button';
    magicLinkBtn.className = 'play-gate__magiclink';
    magicLinkBtn.textContent = 'Email me a link instead';

    card.append(googleBtn, emailBtn, emailForm, codeForm, magicLinkBtn);
  }

  const status = document.createElement('p');
  status.className = 'login-modal__error';
  status.setAttribute('aria-live', 'polite');
  card.append(status);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'login-modal__btn play-gate__close';
  closeBtn.textContent = kind === 'login' ? 'Maybe later' : 'Back to cabinet';
  card.append(closeBtn);

  root.append(card);
  return {
    root, googleBtn, emailBtn, emailForm, emailInput, emailSend,
    codeForm, codeInput, codeVerify, magicLinkBtn, status, closeBtn,
  };
}
